import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Film, Clock, Calendar, Check } from "lucide-react";

export default function ClientOrder() {
  const { t, money } = useLang();
  const [packs, setPacks] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/packages").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  const order = async (p) => {
    setLoadingId(p.id);
    try {
      await api.post("/orders", { client_id: "self", package_id: p.id, status: "PAYEE" });
      toast.success(t("co.saved"));
      navigate("/espace");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoadingId(null); }
  };

  return (
    <div className="ch-fade-up space-y-8">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("co.eyebrow")}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("co.title")}</h1>
        <p className="mt-2 text-sm text-neutral-500">{t("co.subtitle")}</p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {packs.map((p) => (
          <div key={p.id} data-testid={`client-package-${p.id}`} className="flex flex-col rounded-sm border border-neutral-200 p-6 transition-colors hover:border-black">
            <h3 className="font-display text-xl font-bold">{p.name}</h3>
            <p className="mt-3 font-display text-4xl font-extrabold tracking-tight">{money(p.price)}</p>
            <p className="text-sm text-neutral-500">{money(p.price_per_video)} {t("pkg.per_video")}</p>
            {p.description && <p data-testid={`client-package-desc-${p.id}`} className="mt-3 whitespace-pre-line text-sm text-neutral-600">{p.description}</p>}
            <ul className="mt-5 flex-1 space-y-2 border-t border-neutral-100 pt-4 text-sm text-neutral-600">
              <li className="flex items-center gap-2"><Film className="h-4 w-4 text-neutral-400" />{p.video_count} {t("pkg.count_label")}</li>
              <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-neutral-400" />{p.seconds_per_video}s {t("pkg.seconds_label")}</li>
              <li className="flex items-center gap-2"><Calendar className="h-4 w-4 text-neutral-400" />{t("pkg.validity_label")} {p.validity_months} {t("pkg.months")}</li>
            </ul>
            <Button data-testid={`order-package-${p.id}`} onClick={() => order(p)} disabled={loadingId === p.id}
              className="mt-6 w-full rounded-sm bg-black hover:bg-neutral-800">
              {loadingId === p.id ? "…" : <span className="flex items-center gap-2"><Check className="h-4 w-4" /> {t("co.order_btn")}</span>}
            </Button>
          </div>
        ))}
        {packs.length === 0 && <p className="text-sm text-neutral-400">{t("co.empty")}</p>}
      </div>
    </div>
  );
}
